export type Locale = 'en' | 'th' | 'id';

type TranslationTree = {
  [key: string]: string | TranslationTree;
};

export const translations: Record<Locale, TranslationTree> = {
  en: {
    meta: {
      title: 'byt.la – Smart URL Shortener with Analytics, QR & Custom Links',
      description:
        'Shorten links instantly with byt.la. Create branded URLs, track clicks, generate QR codes, and manage campaigns in one fast, secure platform.',
    },
    nav: {
      admin: 'Admin',
      dashboard: 'Dashboard',
      signIn: 'Sign in',
      signUp: 'Sign up',
      signOut: 'Sign out',
      terms: 'Terms',
      privacy: 'Privacy',
      cookies: 'Cookies',
      faq: 'FAQ',
      language: 'Language',
      logoAlt: 'byt.la logo',
      closeMenu: 'Close menu',
    },
    home: {
      title: 'byt.la',
      heroTitle: 'byt.la — fast, secure URL shortening with privacy-first analytics',
      tagline: 'The Modern Link Management Platform',
      signUpCta: 'Create a free account',
      signInCta: 'Sign in to your dashboard',
      longDescription1:
        'byt.la helps you create short, branded links that people trust. Every link is validated to block unsafe destinations, and you can add custom codes, expiration times, and UTM parameters to keep campaigns clean and measurable. The service is built for speed, so redirects happen quickly on mobile and desktop while still protecting your data.',
      longDescription2:
        'When you share a link, we collect only the analytics you need to make better decisions: clicks, referrers, devices, and locations. Bot traffic is detected and filtered so your stats reflect real audiences, and your private data is never sold. You stay in control with dashboards that show performance trends at a glance.',
      longDescription3:
        'Whether you are launching a marketing campaign or just need a clean, memorable link for a document, byt.la is designed to be simple and reliable. The app supports QR codes, dark mode, and a progressive web experience, plus tools for bulk shortening and smart URL rotation as your needs grow.',
      learnAboutLink: 'Learn more about byt.la',
      readFaqLink: 'Read the full FAQ for tips',
      tabSingle: 'URL Shortener',
      tabBulk: 'Bulk URL Shortener',
      tabRotator: 'Smart URL',
      tabRotatorBadge: 'NEW',
      bulkSignIn: 'Please sign in to use the bulk URL shortener.',
      rotatorSignIn: 'Please sign in to use the Smart URL feature.',
      signIn: 'Sign In',
      featuresTitle: 'Features',
      featureCustomTitle: 'Custom Short Codes',
      featureCustomBody: 'Create memorable links with custom aliases',
      featureAnalyticsTitle: 'Analytics & Device Insights',
      featureAnalyticsBody: 'Track clicks, referrers, browsers, OS, and devices',
      featureExpirationTitle: 'Link Expiration',
      featureExpirationBody: 'Set time-based link expiration or keep forever',
      featureBulkTitle: 'Bulk URL Shortener',
      featureBulkBody: 'Shorten up to 20 URLs at once (registered users)',
      featureUtmTitle: 'UTM Builder',
      featureUtmBody: 'Add campaign tracking with presets or manual UTMs',
      featureQrTitle: 'QR Codes & PWA Ready',
      featureQrBody: 'Generate QR codes and install the app on mobile',
      featureFreeTitle: 'Free Forever Plan',
      featureFreeBody: 'Start shortening links with no limits on essential features',
      featureNoCardTitle: 'No Credit Card Required',
      featureNoCardBody: 'Sign up in seconds without billing information',
      sponsorTitle: 'Built with Support from the Best',
      sponsorSubtitle: 'Backed by innovative companies and passionate open source sponsors.',
      sponsorLogoOne: 'Disney',
      sponsorLogoTwo: 'Alibaba',
      sponsorLogoThree: 'Booking.com',
      sponsorLogoFour: 'Red Hat',
      sponsorLogoFive: 'IBM',
      sponsorLogoSix: 'DigitalOcean',
      sponsorLogoSeven: 'Netlify',
      sponsorLogoEight: 'Vercel',
      sponsorLogoNine: 'Salesforce',
      sponsorLogoTen: 'Shopify',
      sponsorLogoEleven: 'Stripe',
    },
    auth: {
      loginTitle: 'Sign in to your account',
      registerTitle: 'Create your account',
      forgotTitle: 'Reset your password',
      forgotSubtitle: 'Enter your email and we’ll send reset instructions.',
      forgotSubmit: 'Send reset link',
      forgotSuccess: 'If an account exists for this email, we’ve sent password reset instructions.',
      forgotBack: 'Remembered your password?',
      noAccount: "Don't have an account?",
      haveAccount: 'Already have an account?',
      email: 'Email address',
      name: 'Full Name',
      password: 'Password',
      passwordHint: 'Must be at least 8 characters',
      confirmPassword: 'Confirm Password',
      rememberMe: 'Remember me',
      forgotPassword: 'Forgot password?',
      signIn: 'Sign in',
      signUp: 'Sign up',
      createAccount: 'Create account',
    },
    footer: {
      tagline: 'Fast, secure, and privacy-focused URL shortening with powerful analytics.',
      madeWith: 'Made with',
      usingNext: 'using Next.js',
      product: 'Product',
      legal: 'Legal',
      support: 'Support',
      home: 'Home',
      about: 'About',
      faq: 'FAQ',
      dashboard: 'Dashboard',
      terms: 'Terms of Service',
      privacy: 'Privacy Policy',
      cookies: 'Cookie Policy',
      reportAbuse: 'Report Abuse',
      helpCenter: 'Help Center',
      github: 'GitHub',
      contactSupport: 'Contact Support',
      rights: 'All rights reserved.',
      status: 'All Systems Operational',
      statusOperational: 'All Systems Operational',
      statusDegraded: 'Degraded Performance',
      statusDown: 'System Outage',
      statusChecking: 'Checking Status...',
      statusTooltip: 'Click to view system status details',
      systemStatus: 'System Status',
      refreshEvery: 'Refreshes every',
    },
    urlForm: {
      enterUrl: 'Enter your long URL',
      showAdvanced: 'Show advanced options',
      hideAdvanced: 'Hide advanced options',
      customCode: 'Custom short code (optional)',
      customHint: '3-20 characters: letters, numbers, hyphens, underscores',
      expiration: 'Expiration Time (Optional)',
      neverExpire: 'Never expire means permanent link',
      shorten: 'Shorten URL',
      shortening: 'Shortening...',
    },
    urlResult: {
      ready: 'Your shortened URL is ready!',
      copied: 'Copied!',
      copy: 'Copy',
      visit: 'Visit URL',
      qr: 'QR Code',
      share: 'Share',
      copyLink: 'Copy Link',
      original: 'Original URL:',
      shortCode: 'Short Code:',
      expires: 'Expires:',
      analytics: 'View Analytics →',
    },
    bulk: {
      title: 'Bulk URL Shortener',
      subtitle: 'Shorten up to 20 URLs at once. Enter one URL per line.',
      urlsLabel: 'URLs to Shorten (one per line)',
      count: 'URLs',
      expiration: 'Expiration Time (Optional)',
      submit: 'Shorten All URLs',
      processing: 'Processing...',
    },
    abuseForm: {
      title: 'Report Submitted',
      thanks: "Thank you for helping keep byt.la safe. We'll review your report shortly.",
      submitAnother: 'Submit Another Report',
      urlLabel: 'Shortened URL',
      urlHelp: 'Enter the full byt.la URL you want to report',
      categoryLabel: 'Abuse Category',
      categoryPlaceholder: 'Select a category...',
      phishing: 'Phishing',
      malware: 'Malware Distribution',
      scam: 'Scam or Fraud',
      spam: 'Spam',
      illegal: 'Illegal Content',
      copyright: 'Copyright/Trademark Violation',
      other: 'Other',
      descriptionLabel: 'Description',
      descriptionHelp: 'Minimum 20 characters',
      descriptionPlaceholder:
        "Please provide details about the abuse, including what happens when you visit the link, why it's harmful, and any other relevant information...",
      emailLabel: 'Your Email',
      emailOptional: '(optional)',
      emailHelp: "Provide your email if you'd like updates on your report",
      submit: 'Submit Abuse Report',
      submitting: 'Submitting Report...',
    },
    settings: {
      title: 'Settings',
      subtitle: 'Update your language and timezone preferences.',
      language: 'Language',
      timezone: 'Timezone',
      save: 'Save changes',
      saving: 'Saving...',
      updated: 'Settings updated.',
      passwordTitle: 'Change Password',
      currentPassword: 'Current Password',
      newPassword: 'New Password',
      confirmPassword: 'Confirm Password',
      passwordSave: 'Update Password',
      passwordSaving: 'Updating...',
      passwordUpdated: 'Password updated.',
      passwordMismatch: 'Passwords do not match.',
    },
    rotator: {
      title: 'Smart URL',
      description: 'Rotate between multiple destination URLs with a single smart link',
      slugLabel: 'Rotator Slug (optional)',
      slugPlaceholder: 'your-rotation',
      slugHelp: 'Leave blank to auto-generate a short rotator link.',
      selectUrl: 'Select URL to add rotation',
      noUrlsAvailable: 'No URLs available. Create a URL first.',
      typeLabel: 'Rotation Type',
      typeSequential: 'Sequential',
      typeRoundRobin: 'Round Robin',
      typeRandom: 'Random',
      typeWeighted: 'Weighted',
      typeSequentialDesc: 'Cycles through URLs in order',
      typeRoundRobinDesc: 'Based on total clicks',
      typeRandomDesc: 'Random selection',
      typeWeightedDesc: 'Probability-based distribution',
      typeSequentialHelp: 'Cycles through destination URLs in order',
      typeRoundRobinHelp: 'Distributes clicks evenly based on total clicks',
      typeRandomHelp: 'Each click chooses a random destination',
      typeWeightedHelp: 'Distribution based on weight (probability)',
      urlsLabel: 'Destination URLs (2-5)',
      urlsPlaceholder: 'Enter destination URL',
      destinationsTitle: 'Destination URLs',
      destinationsHelp: 'Add 2-5 destination URLs. One per line.',
      destinationsPlaceholder:
        'https://example.com/page-1\nhttps://example.com/page-2\nhttps://example.com/page-3',
      destinationsHint:
        'Use line order for sequential rotations. Weighted rotations are evenly distributed.',
      addUrl: 'Add URL',
      removeUrl: 'Remove',
      weightLabel: 'Weight',
      weightHelp: 'Higher weight means more traffic (1-1000)',
      orderLabel: 'Order',
      minUrlsError: 'Minimum 2 URLs required',
      maxUrlsError: 'Maximum 5 URLs allowed',
      invalidUrlError: 'Invalid URL format',
      invalidWeightError: 'Weight must be between 1-1000',
      createButton: 'Create Rotation',
      creating: 'Creating...',
      loading: 'Loading rotation data...',
      updateButton: 'Update Rotation',
      updating: 'Updating...',
      deleteButton: 'Delete Rotation',
      deleting: 'Deleting...',
      created: 'Rotation created successfully',
      successCreated: 'Rotation created successfully',
      updated: 'Rotation updated successfully',
      deleted: 'Rotation deleted successfully',
      error: 'Failed to create rotation',
      errorCreating: 'Failed to create rotation',
      errorUpdating: 'Failed to update rotation',
      active: 'Active',
      inactive: 'Inactive',
      enable: 'Enable',
      disable: 'Disable',
      stats: 'Rotation Statistics',
      totalHits: 'Total Hits',
      distribution: 'Click Distribution',
      lastUsed: 'Last Used URL',
      rotations: 'Rotations',
      noRotations: 'No rotations yet',
      manageRotations: 'Manage Rotations',
      manageTitle: 'Manage Rotations',
      editTitle: 'Edit Smart URL',
      rotationType: 'Type',
      urlCount: 'URLs',
      status: 'Status',
      statusActive: 'Active',
      statusInactive: 'Inactive',
      actions: 'Actions',
      actionEdit: 'Edit',
      actionDelete: 'Delete',
      actionView: 'View',
      deleteConfirm: 'Are you sure you want to delete this rotation?',
      expirationLabel: 'Expiration (Optional)',
      expirationPlaceholder: 'Hours until expiration',
      expirationHelp: 'Leave blank for no expiration',
      utmTitle: 'UTM Parameters (optional)',
      utmHelp: 'These parameters are added to all destination URLs',
      removeDestination: 'Remove destination URL',
      addDestination: 'Add destination URL',
    },
    dashboard: {
      title: 'Dashboard',
      welcome: 'Welcome back',
      signOut: 'Sign out',
      settings: 'Account Settings',
      totalLinks: 'Total Links',
      totalClicks: 'Total Clicks',
      activeLinks: 'Active Links',
      topBrowsers: 'Top Browsers',
      topOS: 'Top Operating Systems',
      topDevices: 'Top Device Types',
      yourLinks: 'Your Links',
      createNew: 'Create New',
      back: 'Back to Dashboard',
      noData: 'No data yet.',
      noLinksTitle: "You haven't created any links yet",
      noLinksCta: 'Create Your First Link',
      searchPlaceholder: 'Search by short code or URL...',
      clear: 'Clear',
      foundPrefix: 'Found',
      foundOf: 'of',
      foundLinkSingular: 'link',
      foundLinkPlural: 'links',
      noMatches: 'No links found matching',
      tableShortCode: 'Short Code',
      tableOriginalUrl: 'Original URL',
      tableClicks: 'Clicks',
      tableCreated: 'Created',
      tableStatus: 'Status',
      tableActions: 'Actions',
      statusExpired: 'Expired',
      statusActive: 'Active',
      actionSave: 'Save',
      actionCancel: 'Cancel',
      actionCopy: 'Copy',
      actionCopied: 'Copied!',
      actionQr: 'QR',
      actionEdit: 'Edit',
      actionManageRotator: 'Manage Rotator',
      actionAnalytics: 'Analytics',
      actionDelete: 'Delete',
      actionDeleting: 'Deleting...',
      confirmTitle: 'Confirm Delete',
      deleteConfirmPrefix: 'Are you sure you want to delete',
      deleteConfirmSuffix: 'This cannot be undone.',
      unknown: 'Unknown',
    },
    analytics: {
      accessDeniedTitle: 'Access Denied',
      accessDeniedAuth: 'Authentication required. Please sign in to view analytics.',
      accessDeniedOwner: 'Access denied. You can only view analytics for your own URLs.',
      signIn: 'Sign In',
      myDashboard: 'My Dashboard',
      backToDashboard: 'Back to Dashboard',
      titlePrefix: 'Analytics for',
      rotatorBadge: 'Rotator',
      rotatorDestinationsTitle: 'Rotation Destinations',
      rotatorDestinationsToggle: 'View rotation destinations',
      rotatorHitsLabel: 'Hits',
      totalClicks: 'Total Clicks',
      created: 'Created',
      status: 'Status',
      statusActive: 'Active',
      statusExpired: 'Expired',
      statusPermanent: 'Permanent',
      expires: 'Expires',
      utmTitle: 'UTM Parameters',
      utmNone: 'No UTM parameters set for this link.',
      destinationUrl: 'Destination URL',
      clicksByDay: 'Clicks by Day',
      topReferrers: 'Top Referrers',
      topBrowsers: 'Top Browsers',
      topOS: 'Top Operating Systems',
      topDevices: 'Top Device Types',
      noBrowserData: 'No browser data yet.',
      noOsData: 'No OS data yet.',
      noDeviceData: 'No device data yet.',
      recentClicks: 'Recent Clicks',
      tableTimestamp: 'Timestamp',
      tableIp: 'IP Address',
      tableReferrer: 'Referrer',
      tableLanguage: 'Language',
      tableBrowser: 'Browser',
      tableOs: 'Operating System',
      tableDevice: 'Device Type',
      tableUserAgent: 'User Agent',
      direct: 'Direct',
      unknown: 'Unknown',
      expandRow: 'Expand row',
      collapseRow: 'Collapse row',
      searchPlaceholder: 'Search recent clicks...',
      clear: 'Clear',
      foundPrefix: 'Found',
      foundOf: 'of',
      foundClickSingular: 'click',
      foundClickPlural: 'clicks',
      noMatches: 'No clicks found matching',
      loading: 'Loading...',
      prev: 'Previous',
      next: 'Next',
      page: 'Page',
      of: 'of',
      showing: 'Showing',
      to: 'to',
      results: 'results',
      tableTime: 'Time',
      tableClickId: 'Click ID',
      tableLocation: 'Location',
      tableCountry: 'Country',
      tableTraffic: 'Traffic',
      human: 'Human',
      bot: 'Bot',
      trafficAll: 'All',
      trafficHumans: 'Humans',
      trafficBots: 'Bots',
      allFields: 'All fields',
      clearFilters: 'Clear filters',
      noClicks: 'No clicks found.',
      loadFailed: 'Failed to load clicks.',
      filterByValue: 'Filter by this value',
      copy: 'Copy',
      copied: 'Copied',
    },
    adminUsers: {
      title: 'Manage Users',
      subtitle: 'Create, update, or remove users.',
      backToAdmin: 'Back to Admin',
      createTitle: 'Create User',
      email: 'Email',
      name: 'Name',
      password: 'Password',
      role: 'Role',
      roleUser: 'User',
      roleAdmin: 'Admin',
      createButton: 'Create User',
      allTitle: 'All Users',
      searchPlaceholder: 'Search users...',
      clear: 'Clear',
      tableEmail: 'Email',
      tableName: 'Name',
      tableRole: 'Role',
      tableCreated: 'Created',
      tableActions: 'Actions',
      actionSave: 'Save',
      actionCancel: 'Cancel',
      actionEdit: 'Edit',
      actionDelete: 'Delete',
      noUsers: 'No users found.',
      deleteConfirmPrefix: 'Delete user',
      deleteConfirmSuffix: 'This cannot be undone.',
      confirmTitle: 'Confirm Delete',
    },
    adminLinks: {
      title: 'Manage Links',
      subtitle: 'Create, update, or remove shortened URLs.',
      backToAdmin: 'Back to Admin',
      createTitle: 'Create Link',
      enterUrl: 'Enter your long URL',
      showAdvanced: 'Show advanced options',
      hideAdvanced: 'Hide advanced options',
      customCode: 'Custom short code (optional)',
      customHint: '3-20 characters: letters, numbers, hyphens, underscores',
      owner: 'Owner (optional)',
      anonymous: 'Anonymous',
      expiration: 'Expiration Time (Optional)',
      neverExpire: 'Never expire',
      neverExpireNote: 'Never expire means permanent link',
      utmTitle: 'UTM Parameters (optional)',
      utmPresets: 'Popular presets',
      utmNote: 'UTMs are appended to your destination URL for campaign tracking.',
      utmMediumHelp: 'Channels: cpc (paid ads), email, social, organic, affiliate, referral',
      utmCampaignHelp:
        'Descriptive names: spring_sale_2026, product_launch_x, newsletter_q1, black_friday',
      utmTermHelp: 'Target Keywords: running+shoes, marketing+software',
      utmContentHelp: 'Element descriptors: header_button, text_link, banner_ad_blue, video_ad',
      presetManual: 'Manual / None',
      presetNewsletter: 'Newsletter (email)',
      presetFacebook: 'Facebook (social)',
      presetFacebookAds: 'Facebook Ads (paid social)',
      presetInstagram: 'Instagram (social)',
      presetX: 'X / Twitter (social)',
      presetLinkedIn: 'LinkedIn (social)',
      presetGoogleAds: 'Google Ads (cpc)',
      presetWhatsapp: 'WhatsApp (messaging)',
      presetTelegram: 'Telegram (messaging)',
      presetLine: 'Line Messenger (messaging)',
      presetTiktokAds: 'TikTok Ads (paid social)',
      presetTiktokSocial: 'TikTok Social (social)',
      createButton: 'Create Link',
      creating: 'Creating...',
      bulkTitle: 'Bulk URL Shortener',
      bulkSubtitle: 'Shorten up to 50 URLs at once. Enter one URL per line.',
      bulkLabel: 'URLs to Shorten (one per line)',
      bulkCountSuffix: 'URLs',
      bulkSubmit: 'Shorten All URLs',
      bulkProcessing: 'Processing...',
      bulkErrorEmpty: 'Please enter at least one URL',
      bulkErrorLimit: 'Maximum 50 URLs allowed at once',
      bulkSuccessPrefix: 'Successfully shortened',
      bulkSuccessSuffix: 'URL',
      bulkSuccessSuffixPlural: 'URLs',
      copyAll: 'Copy All',
      bulkFailedSingular: 'URL failed',
      bulkFailedPlural: 'URLs failed',
      allLinksTitle: 'All Links',
      searchPlaceholder: 'Search by code, URL, or owner...',
      clear: 'Clear',
      tableShortCode: 'Short Code',
      tableOriginalUrl: 'Original URL',
      tableOwner: 'Owner',
      tableClicks: 'Clicks',
      tableCreated: 'Created',
      tableActions: 'Actions',
      actionSave: 'Save',
      actionCancel: 'Cancel',
      actionEdit: 'Edit',
      actionDelete: 'Delete',
      actionQr: 'QR',
      actionAnalytics: 'Analytics',
      deleteConfirmPrefix: 'Delete',
      deleteConfirmSuffix: 'This cannot be undone.',
      noLinks: 'No links found.',
      expiresPlaceholder: 'Expires (hrs)',
      directCopy: 'Copy',
      confirmTitle: 'Confirm Delete',
    },
    error: {
      title: 'Link Not Found',
      subtitle: "Sorry, the short link you're looking for doesn't exist or may have been removed.",
      whyTitle: 'Why might this happen?',
      reasonTyped: 'The link was typed incorrectly or is incomplete',
      reasonDeleted: 'The link was deleted by its owner',
      reasonMissing: 'The link never existed in our system',
      goHome: 'Go Home',
      createLink: 'Create a Link',
    },
  },
  th: {
    meta: {
      title: 'byt.la – ย่อลิงก์อัจฉริยะพร้อมสถิติ QR และลิงก์กำหนดเอง',
      description:
        'ย่อลิงก์ได้ทันทีด้วย byt.la สร้างลิงก์แบรนด์ ติดตามคลิก สร้าง QR และจัดการแคมเปญในแพลตฟอร์มที่รวดเร็วและปลอดภัย',
    },
    nav: {
      admin: 'แอดมิน',
      dashboard: 'แดชบอร์ด',
      signIn: 'เข้าสู่ระบบ',
      signUp: 'สมัครสมาชิก',
      signOut: 'ออกจากระบบ',
      terms: 'ข้อกำหนด',
      privacy: 'ความเป็นส่วนตัว',
      cookies: 'คุกกี้',
      faq: 'คำถามที่พบบ่อย',
      language: 'ภาษา',
      logoAlt: 'โลโก้ byt.la',
      closeMenu: 'ปิดเมนู',
    },
    home: {
      title: 'byt.la',
      heroTitle: 'byt.la — ย่อลิงก์รวดเร็ว ปลอดภัย พร้อมสถิติที่เคารพความเป็นส่วนตัว',
      tagline: 'แพลตฟอร์มจัดการลิงก์สมัยใหม่',
      signUpCta: 'สร้างบัญชีฟรี',
      signInCta: 'เข้าสู่ระบบแดชบอร์ดของคุณ',
      longDescription1:
        'byt.la ช่วยให้คุณสร้างลิงก์สั้นที่ดูน่าเชื่อถือ พร้อมตั้งชื่อเองได้ หมดอายุได้ และเพิ่ม UTM เพื่อวัดผลแคมเปญอย่างเป็นระบบ เราตรวจสอบปลายทางเพื่อบล็อกลิงก์ที่ไม่ปลอดภัย และให้การเปลี่ยนเส้นทางที่รวดเร็วทั้งบนมือถือและเดสก์ท็อป',
      longDescription2:
        'เมื่อคุณแชร์ลิงก์ เราบันทึกเฉพาะสถิติที่จำเป็น เช่น จำนวนคลิก แหล่งที่มา อุปกรณ์ และพื้นที่ใช้งาน พร้อมระบบตรวจจับบอตเพื่อให้ตัวเลขสะท้อนผู้ชมจริง ข้อมูลส่วนตัวของคุณจะไม่ถูกขายหรือใช้เกินความจำเป็น',
      longDescription3:
        'ไม่ว่าคุณจะโปรโมตแคมเปญการตลาด หรือแชร์เอกสารสำคัญ byt.la ถูกออกแบบให้ใช้งานง่ายและเชื่อถือได้ มี QR Code, โหมดมืด และประสบการณ์แบบแอป รวมถึงเครื่องมือย่อลิงก์หลายรายการและ Smart URL เพื่อรองรับการเติบโตในอนาคต',
      learnAboutLink: 'รู้จัก byt.la ให้มากขึ้น',
      readFaqLink: 'อ่านคำถามที่พบบ่อยแบบละเอียด',
      tabSingle: 'ย่อลิงก์',
      tabBulk: 'ย่อลิงก์หลายรายการ',
      tabRotator: 'Smart URL',
      tabRotatorBadge: 'ใหม่',
      bulkSignIn: 'กรุณาเข้าสู่ระบบเพื่อใช้งานการย่อลิงก์หลายรายการ',
      rotatorSignIn: 'กรุณาเข้าสู่ระบบเพื่อใช้งาน Smart URL',
      signIn: 'เข้าสู่ระบบ',
      featuresTitle: 'คุณสมบัติ',
      featureCustomTitle: 'โค้ดสั้นแบบกำหนดเอง',
      featureCustomBody: 'สร้างลิงก์ที่จดจำง่ายด้วยชื่อเฉพาะ',
      featureAnalyticsTitle: 'สถิติและอุปกรณ์',
      featureAnalyticsBody: 'ติดตามคลิก แหล่งที่มา เบราว์เซอร์ ระบบ และอุปกรณ์',
      featureExpirationTitle: 'หมดอายุลิงก์',
      featureExpirationBody: 'ตั้งเวลาหมดอายุหรือใช้ถาวร',
      featureBulkTitle: 'ย่อลิงก์หลายรายการ',
      featureBulkBody: 'ย่อลิงก์ได้สูงสุด 20 รายการ (สำหรับสมาชิก)',
      featureUtmTitle: 'ตัวช่วย UTM',
      featureUtmBody: 'เพิ่มแคมเปญด้วยค่าที่กำหนดหรือกรอกเอง',
      featureQrTitle: 'QR และ PWA',
      featureQrBody: 'สร้าง QR และติดตั้งเป็นแอปบนมือถือ',
      featureFreeTitle: 'แพ็กเกจฟรีตลอดไป',
      featureFreeBody: 'เริ่มใช้งานได้ทันทีโดยไม่จำกัดฟีเจอร์หลัก',
      featureNoCardTitle: 'ไม่ต้องใช้บัตรเครดิต',
      featureNoCardBody: 'สมัครใช้งานได้ทันทีโดยไม่ต้องกรอกข้อมูลชำระเงิน',
      sponsorTitle: 'ได้รับการสนับสนุนจากทีมที่เราชื่นชม',
      sponsorSubtitle: 'รายชื่อพันธมิตรและผู้สนับสนุนด้านเทคโนโลยี',
      sponsorLogoOne: 'Disney',
      sponsorLogoTwo: 'Alibaba',
      sponsorLogoThree: 'Booking.com',
      sponsorLogoFour: 'Red Hat',
      sponsorLogoFive: 'IBM',
      sponsorLogoSix: 'DigitalOcean',
      sponsorLogoSeven: 'Netlify',
      sponsorLogoEight: 'Vercel',
      sponsorLogoNine: 'Salesforce',
      sponsorLogoTen: 'Shopify',
      sponsorLogoEleven: 'Stripe',
    },
    auth: {
      loginTitle: 'เข้าสู่ระบบบัญชีของคุณ',
      registerTitle: 'สร้างบัญชีของคุณ',
      forgotTitle: 'รีเซ็ตรหัสผ่าน',
      forgotSubtitle: 'กรอกอีเมลเพื่อรับลิงก์รีเซ็ตรหัสผ่าน',
      forgotSubmit: 'ส่งลิงก์รีเซ็ต',
      forgotSuccess: 'หากมีบัญชีนี้อยู่ เราได้ส่งลิงก์รีเซ็ตให้แล้ว',
      forgotBack: 'จำรหัสผ่านได้แล้ว?',
      noAccount: 'ยังไม่มีบัญชีใช่ไหม?',
      haveAccount: 'มีบัญชีอยู่แล้วใช่ไหม?',
      email: 'อีเมล',
      name: 'ชื่อเต็ม',
      password: 'รหัสผ่าน',
      passwordHint: 'ต้องมีอย่างน้อย 8 ตัวอักษร',
      confirmPassword: 'ยืนยันรหัสผ่าน',
      rememberMe: 'จดจำฉัน',
      forgotPassword: 'ลืมรหัสผ่าน?',
      signIn: 'เข้าสู่ระบบ',
      signUp: 'สมัครสมาชิก',
      createAccount: 'สร้างบัญชี',
    },
    footer: {
      tagline: 'ย่อลิงก์ที่รวดเร็ว ปลอดภัย และคำนึงถึงความเป็นส่วนตัว พร้อมสถิติครบถ้วน',
      madeWith: 'สร้างด้วย',
      usingNext: 'โดยใช้ Next.js',
      product: 'ผลิตภัณฑ์',
      legal: 'กฎหมาย',
      support: 'ช่วยเหลือ',
      home: 'หน้าแรก',
      about: 'เกี่ยวกับเรา',
      faq: 'คำถามที่พบบ่อย',
      dashboard: 'แดชบอร์ด',
      terms: 'ข้อกำหนดในการให้บริการ',
      privacy: 'นโยบายความเป็นส่วนตัว',
      cookies: 'นโยบายคุกกี้',
      reportAbuse: 'รายงานการละเมิด',
      helpCenter: 'ศูนย์ช่วยเหลือ',
      github: 'GitHub',
      contactSupport: 'ติดต่อฝ่ายสนับสนุน',
      rights: 'สงวนลิขสิทธิ์',
      status: 'ระบบทำงานปกติ',
      statusOperational: 'ระบบทำงานปกติทุกอย่าง',
      statusDegraded: 'ประสิทธิภาพลดลง',
      statusDown: 'ระบบขัดข้อง',
      statusChecking: 'กำลังตรวจสอบสถานะ...',
      statusTooltip: 'คลิกเพื่อดูรายละเอียดสถานะระบบ',
      systemStatus: 'สถานะระบบ',
      refreshEvery: 'รีเฟรชทุก',
    },
    urlForm: {
      enterUrl: 'กรอกลิงก์ยาวของคุณ',
      showAdvanced: 'แสดงตัวเลือกขั้นสูง',
      hideAdvanced: 'ซ่อนตัวเลือกขั้นสูง',
      customCode: 'โค้ดย่อแบบกำหนดเอง (ไม่บังคับ)',
      customHint: '3-20 ตัวอักษร: ตัวอักษร ตัวเลข ขีดกลาง ขีดล่าง',
      expiration: 'เวลาหมดอายุ (ไม่บังคับ)',
      neverExpire: 'ไม่หมดอายุหมายถึงลิงก์ถาวร',
      shorten: 'ย่อลิงก์',
      shortening: 'กำลังย่อ...',
    },
    urlResult: {
      ready: 'ลิงก์ย่อของคุณพร้อมแล้ว!',
      copied: 'คัดลอกแล้ว!',
      copy: 'คัดลอก',
      visit: 'เปิดลิงก์',
      qr: 'คิวอาร์โค้ด',
      share: 'แชร์',
      copyLink: 'คัดลอกลิงก์',
      original: 'ลิงก์ต้นฉบับ:',
      shortCode: 'โค้ดย่อ:',
      expires: 'หมดอายุ:',
      analytics: 'ดูสถิติ →',
    },
    bulk: {
      title: 'ย่อลิงก์หลายรายการ',
      subtitle: 'ย่อได้สูงสุด 20 ลิงก์ ใส่หนึ่งลิงก์ต่อบรรทัด',
      urlsLabel: 'ลิงก์ที่ต้องการย่อ (หนึ่งบรรทัดต่อหนึ่งลิงก์)',
      count: 'ลิงก์',
      expiration: 'เวลาหมดอายุ (ไม่บังคับ)',
      submit: 'ย่อลิงก์ทั้งหมด',
      processing: 'กำลังประมวลผล...',
    },
    abuseForm: {
      title: 'ส่งรายงานแล้ว',
      thanks: 'ขอบคุณที่ช่วยให้ byt.la ปลอดภัย เราจะตรวจสอบเร็ว ๆ นี้',
      submitAnother: 'ส่งรายงานอีกครั้ง',
      urlLabel: 'ลิงก์ย่อ',
      urlHelp: 'กรอกลิงก์ byt.la ที่ต้องการรายงาน',
      categoryLabel: 'ประเภทการละเมิด',
      categoryPlaceholder: 'เลือกประเภท...',
      phishing: 'ฟิชชิง',
      malware: 'มัลแวร์',
      scam: 'หลอกลวง/ฉ้อโกง',
      spam: 'สแปม',
      illegal: 'เนื้อหาผิดกฎหมาย',
      copyright: 'ละเมิดลิขสิทธิ์/เครื่องหมายการค้า',
      other: 'อื่น ๆ',
      descriptionLabel: 'รายละเอียด',
      descriptionHelp: 'อย่างน้อย 20 ตัวอักษร',
      descriptionPlaceholder: 'โปรดอธิบายรายละเอียดเกี่ยวกับการละเมิด...',
      emailLabel: 'อีเมลของคุณ',
      emailOptional: '(ไม่บังคับ)',
      emailHelp: 'ใส่อีเมลหากต้องการรับอัปเดต',
      submit: 'ส่งรายงานการละเมิด',
      submitting: 'กำลังส่งรายงาน...',
    },
    settings: {
      title: 'การตั้งค่า',
      subtitle: 'อัปเดตภาษาและเขตเวลา',
      language: 'ภาษา',
      timezone: 'เขตเวลา',
      save: 'บันทึกการเปลี่ยนแปลง',
      saving: 'กำลังบันทึก...',
      updated: 'อัปเดตการตั้งค่าแล้ว',
      passwordTitle: 'เปลี่ยนรหัสผ่าน',
      currentPassword: 'รหัสผ่านปัจจุบัน',
      newPassword: 'รหัสผ่านใหม่',
      confirmPassword: 'ยืนยันรหัสผ่านใหม่',
      passwordSave: 'อัปเดตรหัสผ่าน',
      passwordSaving: 'กำลังอัปเดต...',
      passwordUpdated: 'อัปเดตรหัสผ่านแล้ว',
      passwordMismatch: 'รหัสผ่านไม่ตรงกัน',
    },
    rotator: {
      title: 'Smart URL',
      description: 'หมุนเวียนระหว่าง URL ปลายทางหลายรายการด้วยลิงก์เดียว',
      slugLabel: 'สลักลิงก์หมุนเวียน (ไม่บังคับ)',
      slugPlaceholder: 'rotation-ของคุณ',
      slugHelp: 'เว้นว่างเพื่อให้ระบบสร้างลิงก์หมุนเวียนให้อัตโนมัติ',
      selectUrl: 'เลือก URL เพื่อเพิ่มการหมุนเวียน',
      noUrlsAvailable: 'ไม่มี URL ที่ใช้ได้ สร้าง URL ก่อน',
      typeLabel: 'ประเภทการหมุนเวียน',
      typeSequential: 'ตามลำดับ',
      typeRoundRobin: 'แบบวนรอบ',
      typeRandom: 'แบบสุ่ม',
      typeWeighted: 'แบบน้ำหนัก',
      typeSequentialDesc: 'วนผ่าน URL ตามลำดับ',
      typeRoundRobinDesc: 'ตามจำนวนคลิกทั้งหมด',
      typeRandomDesc: 'เลือกแบบสุ่ม',
      typeWeightedDesc: 'การกระจายตามความน่าจะเป็น',
      typeSequentialHelp: 'วนผ่าน URL ปลายทางตามลำดับ',
      typeRoundRobinHelp: 'กระจายคลิกอย่างสม่ำเสมอตามจำนวนคลิกทั้งหมด',
      typeRandomHelp: 'แต่ละคลิกสุ่ม URL ปลายทาง',
      typeWeightedHelp: 'กระจายตามน้ำหนักความน่าจะเป็น',
      urlsLabel: 'URL ปลายทาง (2-5)',
      urlsPlaceholder: 'ใส่ URL ปลายทาง',
      destinationsTitle: 'URL ปลายทาง',
      destinationsHelp: 'เพิ่ม 2-5 URL ปลายทาง โดยให้ 1 URL ต่อบรรทัด',
      destinationsPlaceholder:
        'https://example.com/page-1\nhttps://example.com/page-2\nhttps://example.com/page-3',
      destinationsHint: 'การหมุนเวียนตามลำดับใช้ลำดับบรรทัดเป็นหลัก',
      addUrl: 'เพิ่ม URL',
      removeUrl: 'ลบ',
      weightLabel: 'น้ำหนัก',
      weightHelp: 'ค่าน้ำหนักมาก = รับทราฟฟิกมากขึ้น (1-1000)',
      orderLabel: 'ลำดับ',
      minUrlsError: 'ต้องมี URL อย่างน้อย 2 รายการ',
      maxUrlsError: 'URL สูงสุด 5 รายการ',
      invalidUrlError: 'รูปแบบ URL ไม่ถูกต้อง',
      invalidWeightError: 'น้ำหนักต้องอยู่ระหว่าง 1-1000',
      createButton: 'สร้างการหมุนเวียน',
      creating: 'กำลังสร้าง...',
      loading: 'กำลังโหลดข้อมูลการหมุนเวียน...',
      updateButton: 'อัปเดตการหมุนเวียน',
      updating: 'กำลังอัปเดต...',
      deleteButton: 'ลบการหมุนเวียน',
      deleting: 'กำลังลบ...',
      created: 'สร้างการหมุนเวียนสำเร็จ',
      successCreated: 'สร้างการหมุนเวียนสำเร็จ',
      updated: 'อัปเดตการหมุนเวียนสำเร็จ',
      deleted: 'ลบการหมุนเวียนสำเร็จ',
      error: 'ไม่สามารถสร้างการหมุนเวียนได้',
      errorCreating: 'ไม่สามารถสร้างการหมุนเวียนได้',
      errorUpdating: 'ไม่สามารถอัปเดตการหมุนเวียนได้',
      active: 'เปิดใช้งาน',
      inactive: 'ปิดใช้งาน',
      enable: 'เปิดใช้งาน',
      disable: 'ปิดใช้งาน',
      stats: 'สถิติการหมุนเวียน',
      totalHits: 'จำนวนครั้งทั้งหมด',
      distribution: 'การกระจายคลิก',
      lastUsed: 'URL ที่ใช้ล่าสุด',
      rotations: 'การหมุนเวียน',
      noRotations: 'ยังไม่มีการหมุนเวียน',
      manageRotations: 'จัดการการหมุนเวียน',
      manageTitle: 'จัดการการหมุนเวียน',
      editTitle: 'แก้ไข Smart URL',
      rotationType: 'ประเภท',
      urlCount: 'URL',
      status: 'สถานะ',
      statusActive: 'เปิดใช้งาน',
      statusInactive: 'ปิดใช้งาน',
      actions: 'การดำเนินการ',
      actionEdit: 'แก้ไข',
      actionDelete: 'ลบ',
      actionView: 'ดู',
      deleteConfirm: 'คุณแน่ใจหรือไม่ว่าต้องการลบการหมุนเวียนนี้?',
      expirationLabel: 'หมดอายุ (ไม่บังคับ)',
      expirationPlaceholder: 'จำนวนชั่วโมงก่อนหมดอายุ',
      expirationHelp: 'เว้นว่างเพื่อไม่หมดอายุ',
      utmTitle: 'พารามิเตอร์ UTM (ไม่บังคับ)',
      utmHelp: 'พารามิเตอร์นี้จะถูกเพิ่มไปยังทุก URL ปลายทาง',
      removeDestination: 'ลบ URL ปลายทาง',
      addDestination: 'เพิ่ม URL ปลายทาง',
    },
    dashboard: {
      title: 'แดชบอร์ด',
      welcome: 'ยินดีต้อนรับ',
      signOut: 'ออกจากระบบ',
      settings: 'ตั้งค่าบัญชี',
      totalLinks: 'ลิงก์ทั้งหมด',
      totalClicks: 'คลิกทั้งหมด',
      activeLinks: 'ลิงก์ที่ใช้งานอยู่',
      topBrowsers: 'เบราว์เซอร์ยอดนิยม',
      topOS: 'ระบบปฏิบัติการยอดนิยม',
      topDevices: 'อุปกรณ์ยอดนิยม',
      yourLinks: 'ลิงก์ของคุณ',
      createNew: 'สร้างใหม่',
      back: 'กลับไปแดชบอร์ด',
      noData: 'ยังไม่มีข้อมูล',
      noLinksTitle: 'คุณยังไม่ได้สร้างลิงก์',
      noLinksCta: 'สร้างลิงก์แรกของคุณ',
      searchPlaceholder: 'ค้นหาด้วยโค้ดสั้นหรือ URL...',
      clear: 'ล้าง',
      foundPrefix: 'พบ',
      foundOf: 'จาก',
      foundLinkSingular: 'ลิงก์',
      foundLinkPlural: 'ลิงก์',
      noMatches: 'ไม่พบลิงก์ที่ตรงกับ',
      tableShortCode: 'โค้ดสั้น',
      tableOriginalUrl: 'URL ต้นฉบับ',
      tableClicks: 'คลิก',
      tableCreated: 'สร้างเมื่อ',
      tableStatus: 'สถานะ',
      tableActions: 'การทำงาน',
      statusExpired: 'หมดอายุ',
      statusActive: 'ใช้งานได้',
      actionSave: 'บันทึก',
      actionCancel: 'ยกเลิก',
      actionCopy: 'คัดลอก',
      actionCopied: 'คัดลอกแล้ว!',
      actionQr: 'QR',
      actionEdit: 'แก้ไข',
      actionManageRotator: 'จัดการหมุนเวียน',
      actionAnalytics: 'สถิติ',
      actionDelete: 'ลบ',
      actionDeleting: 'กำลังลบ...',
      confirmTitle: 'ยืนยันการลบ',
      deleteConfirmPrefix: 'ยืนยันการลบ',
      deleteConfirmSuffix: 'การลบนี้ไม่สามารถกู้คืนได้',
      unknown: 'ไม่ทราบ',
    },
    analytics: {
      accessDeniedTitle: 'ไม่อนุญาตให้เข้าถึง',
      accessDeniedAuth: 'ต้องเข้าสู่ระบบเพื่อดูสถิติ',
      accessDeniedOwner: 'ไม่อนุญาตให้เข้าถึง คุณดูสถิติได้เฉพาะลิงก์ของคุณเท่านั้น',
      signIn: 'เข้าสู่ระบบ',
      myDashboard: 'แดชบอร์ดของฉัน',
      backToDashboard: 'กลับไปแดชบอร์ด',
      titlePrefix: 'สถิติสำหรับ',
      rotatorBadge: 'หมุนเวียน',
      rotatorDestinationsTitle: 'ปลายทางการหมุนเวียน',
      rotatorDestinationsToggle: 'ดูปลายทางการหมุนเวียน',
      rotatorHitsLabel: 'จำนวนครั้ง',
      totalClicks: 'คลิกทั้งหมด',
      created: 'สร้างเมื่อ',
      status: 'สถานะ',
      statusActive: 'ใช้งานได้',
      statusExpired: 'หมดอายุ',
      statusPermanent: 'ถาวร',
      expires: 'หมดอายุ',
      utmTitle: 'พารามิเตอร์ UTM',
      utmNone: 'ลิงก์นี้ไม่มีพารามิเตอร์ UTM',
      destinationUrl: 'ปลายทาง URL',
      clicksByDay: 'คลิกต่อวัน',
      topReferrers: 'แหล่งที่มายอดนิยม',
      topBrowsers: 'เบราว์เซอร์ยอดนิยม',
      topOS: 'ระบบปฏิบัติการยอดนิยม',
      topDevices: 'อุปกรณ์ยอดนิยม',
      noBrowserData: 'ยังไม่มีข้อมูลเบราว์เซอร์',
      noOsData: 'ยังไม่มีข้อมูลระบบปฏิบัติการ',
      noDeviceData: 'ยังไม่มีข้อมูลอุปกรณ์',
      recentClicks: 'คลิกล่าสุด',
      tableTimestamp: 'เวลา',
      tableIp: 'ที่อยู่ IP',
      tableReferrer: 'แหล่งที่มา',
      tableLanguage: 'ภาษา',
      tableBrowser: 'เบราว์เซอร์',
      tableOs: 'ระบบปฏิบัติการ',
      tableDevice: 'ประเภทอุปกรณ์',
      tableUserAgent: 'ยูสเซอร์เอเจนต์',
      direct: 'ตรง',
      unknown: 'ไม่ทราบ',
      expandRow: 'ขยายแถว',
      collapseRow: 'ย่อแถว',
      searchPlaceholder: 'ค้นหาคลิกล่าสุด...',
      clear: 'ล้าง',
      foundPrefix: 'พบ',
      foundOf: 'จาก',
      foundClickSingular: 'คลิก',
      foundClickPlural: 'คลิก',
      noMatches: 'ไม่พบคลิกที่ตรงกับ',
      loading: 'กำลังโหลด...',
      prev: 'ก่อนหน้า',
      next: 'ถัดไป',
      page: 'หน้า',
      of: 'จาก',
      showing: 'แสดง',
      to: 'ถึง',
      results: 'รายการ',
      tableTime: 'เวลา',
      tableClickId: 'รหัสคลิก',
      tableLocation: 'ตำแหน่ง',
      tableCountry: 'ประเทศ',
      tableTraffic: 'ประเภทการเข้าชม',
      human: 'มนุษย์',
      bot: 'บอท',
      trafficAll: 'ทั้งหมด',
      trafficHumans: 'มนุษย์',
      trafficBots: 'บอท',
      allFields: 'ทุกฟิลด์',
      clearFilters: 'ล้างตัวกรอง',
      noClicks: 'ไม่พบคลิก',
      loadFailed: 'โหลดข้อมูลคลิกไม่สำเร็จ',
      filterByValue: 'กรองด้วยค่านี้',
      copy: 'คัดลอก',
      copied: 'คัดลอกแล้ว',
    },
    adminUsers: {
      title: 'จัดการผู้ใช้',
      subtitle: 'สร้าง แก้ไข หรือ ลบผู้ใช้',
      backToAdmin: 'กลับไปแอดมิน',
      createTitle: 'สร้างผู้ใช้',
      email: 'อีเมล',
      name: 'ชื่อ',
      password: 'รหัสผ่าน',
      role: 'บทบาท',
      roleUser: 'ผู้ใช้',
      roleAdmin: 'แอดมิน',
      createButton: 'สร้างผู้ใช้',
      allTitle: 'ผู้ใช้ทั้งหมด',
      searchPlaceholder: 'ค้นหาผู้ใช้...',
      clear: 'ล้าง',
      tableEmail: 'อีเมล',
      tableName: 'ชื่อ',
      tableRole: 'บทบาท',
      tableCreated: 'สร้างเมื่อ',
      tableActions: 'การทำงาน',
      actionSave: 'บันทึก',
      actionCancel: 'ยกเลิก',
      actionEdit: 'แก้ไข',
      actionDelete: 'ลบ',
      noUsers: 'ไม่พบผู้ใช้',
      deleteConfirmPrefix: 'ลบผู้ใช้',
      deleteConfirmSuffix: 'การลบนี้ไม่สามารถกู้คืนได้',
      confirmTitle: 'ยืนยันการลบ',
    },
    adminLinks: {
      title: 'จัดการลิงก์',
      subtitle: 'สร้าง แก้ไข หรือ ลบลิงก์ย่อ',
      backToAdmin: 'กลับไปแอดมิน',
      createTitle: 'สร้างลิงก์',
      enterUrl: 'กรอก URL ยาว',
      showAdvanced: 'แสดงตัวเลือกขั้นสูง',
      hideAdvanced: 'ซ่อนตัวเลือกขั้นสูง',
      customCode: 'โค้ดสั้นแบบกำหนดเอง (ไม่บังคับ)',
      customHint: '3-20 ตัวอักษร: ตัวอักษร ตัวเลข ขีดกลาง ขีดล่าง',
      owner: 'เจ้าของ (ไม่บังคับ)',
      anonymous: 'ไม่ระบุ',
      expiration: 'เวลาหมดอายุ (ไม่บังคับ)',
      neverExpire: 'ไม่หมดอายุ',
      neverExpireNote: 'ไม่หมดอายุหมายถึงลิงก์ถาวร',
      utmTitle: 'พารามิเตอร์ UTM (ไม่บังคับ)',
      utmPresets: 'พรีเซ็ตยอดนิยม',
      utmNote: 'พารามิเตอร์ UTM จะถูกต่อท้าย URL ปลายทางสำหรับการติดตามแคมเปญ',
      utmMediumHelp: 'ช่องทาง: cpc (โฆษณาแบบชำระเงิน), email, social, organic, affiliate, referral',
      utmCampaignHelp:
        'ชื่อแคมเปญ: spring_sale_2026, product_launch_x, newsletter_q1, black_friday',
      utmTermHelp: 'คำค้นเป้าหมาย: running+shoes, marketing+software',
      utmContentHelp: 'ตัวระบุองค์ประกอบ: header_button, text_link, banner_ad_blue, video_ad',
      presetManual: 'กรอกเอง / ไม่ใช้',
      presetNewsletter: 'Newsletter (email)',
      presetFacebook: 'Facebook (social)',
      presetFacebookAds: 'Facebook Ads (paid social)',
      presetInstagram: 'Instagram (social)',
      presetX: 'X / Twitter (social)',
      presetLinkedIn: 'LinkedIn (social)',
      presetGoogleAds: 'Google Ads (cpc)',
      presetWhatsapp: 'WhatsApp (messaging)',
      presetTelegram: 'Telegram (messaging)',
      presetLine: 'Line Messenger (messaging)',
      presetTiktokAds: 'TikTok Ads (paid social)',
      presetTiktokSocial: 'TikTok Social (social)',
      createButton: 'สร้างลิงก์',
      creating: 'กำลังสร้าง...',
      bulkTitle: 'ย่อลิงก์หลายรายการ',
      bulkSubtitle: 'ย่อได้สูงสุด 50 ลิงก์ต่อครั้ง ใส่หนึ่งบรรทัดต่อหนึ่งลิงก์',
      bulkLabel: 'URL ที่ต้องการย่อ (หนึ่งบรรทัดต่อหนึ่งลิงก์)',
      bulkCountSuffix: 'URL',
      bulkSubmit: 'ย่อลิงก์ทั้งหมด',
      bulkProcessing: 'กำลังประมวลผล...',
      bulkErrorEmpty: 'กรุณาใส่อย่างน้อย 1 URL',
      bulkErrorLimit: 'อนุญาตสูงสุด 50 URL ต่อครั้ง',
      bulkSuccessPrefix: 'ย่อสำเร็จ',
      bulkSuccessSuffix: 'ลิงก์',
      bulkSuccessSuffixPlural: 'ลิงก์',
      copyAll: 'คัดลอกทั้งหมด',
      bulkFailedSingular: 'ลิงก์ล้มเหลว',
      bulkFailedPlural: 'ลิงก์ล้มเหลว',
      allLinksTitle: 'ลิงก์ทั้งหมด',
      searchPlaceholder: 'ค้นหาด้วยโค้ด, URL หรือเจ้าของ...',
      clear: 'ล้าง',
      tableShortCode: 'โค้ดสั้น',
      tableOriginalUrl: 'URL ต้นฉบับ',
      tableOwner: 'เจ้าของ',
      tableClicks: 'คลิก',
      tableCreated: 'สร้างเมื่อ',
      tableActions: 'การทำงาน',
      actionSave: 'บันทึก',
      actionCancel: 'ยกเลิก',
      actionEdit: 'แก้ไข',
      actionDelete: 'ลบ',
      actionQr: 'QR',
      actionAnalytics: 'สถิติ',
      deleteConfirmPrefix: 'ลบ',
      deleteConfirmSuffix: 'การลบนี้ไม่สามารถกู้คืนได้',
      noLinks: 'ไม่พบลิงก์',
      expiresPlaceholder: 'หมดอายุ (ชม.)',
      directCopy: 'คัดลอก',
      confirmTitle: 'ยืนยันการลบ',
    },
    error: {
      title: 'ไม่พบลิงก์',
      subtitle: 'ขออภัย ลิงก์ที่คุณค้นหาไม่มีอยู่หรืออาจถูกลบไปแล้ว',
      whyTitle: 'เกิดจากอะไรได้บ้าง?',
      reasonTyped: 'พิมพ์ลิงก์ไม่ถูกต้องหรือไม่ครบถ้วน',
      reasonDeleted: 'เจ้าของลบลิงก์นี้แล้ว',
      reasonMissing: 'ลิงก์นี้ไม่เคยมีอยู่ในระบบ',
      goHome: 'กลับหน้าแรก',
      createLink: 'สร้างลิงก์',
    },
  },
  id: {
    meta: {
      title: 'byt.la – Pemendek URL pintar dengan analitik, QR & tautan kustom',
      description:
        'Pendekkan tautan secara instan dengan byt.la. Buat URL bermerek, lacak klik, buat QR, dan kelola kampanye dalam satu platform yang cepat dan aman.',
    },
    nav: {
      admin: 'Admin',
      dashboard: 'Dasbor',
      signIn: 'Masuk',
      signUp: 'Daftar',
      signOut: 'Keluar',
      terms: 'Ketentuan',
      privacy: 'Privasi',
      cookies: 'Cookie',
      faq: 'FAQ',
      language: 'Bahasa',
      logoAlt: 'Logo byt.la',
      closeMenu: 'Tutup menu',
    },
    home: {
      title: 'byt.la',
      heroTitle: 'byt.la — pemendek URL cepat dan aman dengan analitik yang menjaga privasi',
      tagline: 'Platform manajemen tautan modern',
      signUpCta: 'Buat akun gratis',
      signInCta: 'Masuk ke dasbor Anda',
      longDescription1:
        'byt.la membantu Anda membuat tautan pendek yang rapi dan tepercaya. Anda bisa memakai kode kustom, mengatur masa berlaku, dan menambahkan parameter UTM agar kampanye lebih terukur. Setiap tujuan divalidasi untuk memblokir tautan berbahaya, sementara pengalihan tetap cepat di perangkat apa pun.',
      longDescription2:
        'Saat tautan dibagikan, kami hanya mengumpulkan analitik yang benar-benar Anda butuhkan: klik, referer, perangkat, dan lokasi. Lalu lintas bot terdeteksi dan difilter agar laporan mencerminkan audiens nyata, dan data pribadi Anda tidak pernah dijual.',
      longDescription3:
        'Baik untuk promosi, berbagi dokumen, atau proyek tim, byt.la dirancang sederhana namun andal. Fitur QR code, mode gelap, dan pengalaman PWA membantu penggunaan sehari-hari, sementara pemendekan massal dan Smart URL siap mendukung kebutuhan yang berkembang.',
      learnAboutLink: 'Pelajari lebih lanjut tentang byt.la',
      readFaqLink: 'Baca FAQ lengkap untuk panduan',
      tabSingle: 'Pemendek URL',
      tabBulk: 'Pemendek URL Massal',
      tabRotator: 'Smart URL',
      tabRotatorBadge: 'BARU',
      bulkSignIn: 'Silakan masuk untuk menggunakan pemendek URL massal.',
      rotatorSignIn: 'Silakan masuk untuk menggunakan fitur Smart URL.',
      signIn: 'Masuk',
      featuresTitle: 'Fitur',
      featureCustomTitle: 'Kode Pendek Kustom',
      featureCustomBody: 'Buat tautan yang mudah diingat dengan alias kustom',
      featureAnalyticsTitle: 'Analitik & Perangkat',
      featureAnalyticsBody: 'Lacak klik, referer, browser, OS, dan perangkat',
      featureExpirationTitle: 'Kedaluwarsa Tautan',
      featureExpirationBody: 'Atur masa berlaku atau permanen',
      featureBulkTitle: 'Pemendek URL Massal',
      featureBulkBody: 'Pendekkan hingga 20 URL sekaligus (pengguna terdaftar)',
      featureUtmTitle: 'Pembuat UTM',
      featureUtmBody: 'Tambahkan pelacakan kampanye dengan preset atau manual',
      featureQrTitle: 'QR & PWA',
      featureQrBody: 'Buat QR dan pasang aplikasi di ponsel',
      featureFreeTitle: 'Paket Gratis Selamanya',
      featureFreeBody: 'Mulai tanpa batas untuk fitur inti',
      featureNoCardTitle: 'Tanpa Kartu Kredit',
      featureNoCardBody: 'Daftar cepat tanpa informasi pembayaran',
      sponsorTitle: 'Didukung oleh tim yang kami kagumi',
      sponsorSubtitle: 'Etalase mitra teknologi dan sponsor.',
      sponsorLogoOne: 'Disney',
      sponsorLogoTwo: 'Alibaba',
      sponsorLogoThree: 'Booking.com',
      sponsorLogoFour: 'Red Hat',
      sponsorLogoFive: 'IBM',
      sponsorLogoSix: 'DigitalOcean',
      sponsorLogoSeven: 'Netlify',
      sponsorLogoEight: 'Vercel',
      sponsorLogoNine: 'Salesforce',
      sponsorLogoTen: 'Shopify',
      sponsorLogoEleven: 'Stripe',
    },
    auth: {
      loginTitle: 'Masuk ke akun Anda',
      registerTitle: 'Buat akun Anda',
      forgotTitle: 'Atur ulang kata sandi',
      forgotSubtitle: 'Masukkan email untuk menerima tautan reset.',
      forgotSubmit: 'Kirim tautan reset',
      forgotSuccess: 'Jika akun tersedia, kami telah mengirim tautan reset.',
      forgotBack: 'Ingat kata sandi?',
      noAccount: 'Belum punya akun?',
      haveAccount: 'Sudah punya akun?',
      email: 'Alamat email',
      name: 'Nama Lengkap',
      password: 'Kata sandi',
      passwordHint: 'Minimal 8 karakter',
      confirmPassword: 'Konfirmasi Kata Sandi',
      rememberMe: 'Ingat saya',
      forgotPassword: 'Lupa kata sandi?',
      signIn: 'Masuk',
      signUp: 'Daftar',
      createAccount: 'Buat akun',
    },
    footer: {
      tagline: 'Pemendek URL yang cepat, aman, dan berfokus pada privasi dengan analitik kuat.',
      madeWith: 'Dibuat dengan',
      usingNext: 'menggunakan Next.js',
      product: 'Produk',
      legal: 'Legal',
      support: 'Dukungan',
      home: 'Beranda',
      about: 'Tentang',
      faq: 'FAQ',
      dashboard: 'Dasbor',
      terms: 'Ketentuan Layanan',
      privacy: 'Kebijakan Privasi',
      cookies: 'Kebijakan Cookie',
      reportAbuse: 'Laporkan Penyalahgunaan',
      helpCenter: 'Pusat Bantuan',
      github: 'GitHub',
      contactSupport: 'Hubungi Dukungan',
      rights: 'Hak cipta dilindungi.',
      status: 'Semua sistem berjalan normal',
      statusOperational: 'Semua Sistem Beroperasi Normal',
      statusDegraded: 'Kinerja Menurun',
      statusDown: 'Gangguan Sistem',
      statusChecking: 'Memeriksa Status...',
      statusTooltip: 'Klik untuk melihat detail status sistem',
      systemStatus: 'Status Sistem',
      refreshEvery: 'Refresh setiap',
    },
    urlForm: {
      enterUrl: 'Masukkan URL panjang Anda',
      showAdvanced: 'Tampilkan opsi lanjutan',
      hideAdvanced: 'Sembunyikan opsi lanjutan',
      customCode: 'Kode pendek kustom (opsional)',
      customHint: '3-20 karakter: huruf, angka, tanda hubung, garis bawah',
      expiration: 'Waktu kedaluwarsa (opsional)',
      neverExpire: 'Tidak kedaluwarsa berarti permanen',
      shorten: 'Pendekkan URL',
      shortening: 'Memproses...',
    },
    urlResult: {
      ready: 'URL pendek Anda siap!',
      copied: 'Tersalin!',
      copy: 'Salin',
      visit: 'Buka URL',
      qr: 'Kode QR',
      share: 'Bagikan',
      copyLink: 'Salin Tautan',
      original: 'URL asli:',
      shortCode: 'Kode pendek:',
      expires: 'Kedaluwarsa:',
      analytics: 'Lihat Analitik →',
    },
    bulk: {
      title: 'Pemendek URL Massal',
      subtitle: 'Pendekkan hingga 20 URL sekaligus. Satu URL per baris.',
      urlsLabel: 'URL untuk dipendekkan (satu per baris)',
      count: 'URL',
      expiration: 'Waktu kedaluwarsa (opsional)',
      submit: 'Pendekkan Semua URL',
      processing: 'Memproses...',
    },
    abuseForm: {
      title: 'Laporan Terkirim',
      thanks: 'Terima kasih telah membantu menjaga byt.la tetap aman.',
      submitAnother: 'Kirim Laporan Lain',
      urlLabel: 'URL Pendek',
      urlHelp: 'Masukkan URL byt.la yang ingin dilaporkan',
      categoryLabel: 'Kategori Penyalahgunaan',
      categoryPlaceholder: 'Pilih kategori...',
      phishing: 'Phishing',
      malware: 'Distribusi Malware',
      scam: 'Penipuan',
      spam: 'Spam',
      illegal: 'Konten Ilegal',
      copyright: 'Pelanggaran Hak Cipta/Merek',
      other: 'Lainnya',
      descriptionLabel: 'Deskripsi',
      descriptionHelp: 'Minimal 20 karakter',
      descriptionPlaceholder: 'Jelaskan detail penyalahgunaan...',
      emailLabel: 'Email Anda',
      emailOptional: '(opsional)',
      emailHelp: 'Masukkan email jika ingin pembaruan',
      submit: 'Kirim Laporan',
      submitting: 'Mengirim laporan...',
    },
    settings: {
      title: 'Pengaturan',
      subtitle: 'Perbarui bahasa dan zona waktu.',
      language: 'Bahasa',
      timezone: 'Zona waktu',
      save: 'Simpan perubahan',
      saving: 'Menyimpan...',
      updated: 'Pengaturan diperbarui.',
      passwordTitle: 'Ubah Kata Sandi',
      currentPassword: 'Kata Sandi Saat Ini',
      newPassword: 'Kata Sandi Baru',
      confirmPassword: 'Konfirmasi Kata Sandi',
      passwordSave: 'Perbarui Kata Sandi',
      passwordSaving: 'Memperbarui...',
      passwordUpdated: 'Kata sandi diperbarui.',
      passwordMismatch: 'Kata sandi tidak cocok.',
    },
    dashboard: {
      title: 'Dasbor',
      welcome: 'Selamat datang kembali',
      signOut: 'Keluar',
      settings: 'Pengaturan Akun',
      totalLinks: 'Total Tautan',
      totalClicks: 'Total Klik',
      activeLinks: 'Tautan Aktif',
      topBrowsers: 'Browser Teratas',
      topOS: 'Sistem Operasi Teratas',
      topDevices: 'Jenis Perangkat Teratas',
      yourLinks: 'Tautan Anda',
      createNew: 'Buat Baru',
      back: 'Kembali ke Dasbor',
      noData: 'Belum ada data.',
      noLinksTitle: 'Anda belum membuat tautan apa pun',
      noLinksCta: 'Buat Tautan Pertama',
      searchPlaceholder: 'Cari berdasarkan kode pendek atau URL...',
      clear: 'Hapus',
      foundPrefix: 'Ditemukan',
      foundOf: 'dari',
      foundLinkSingular: 'tautan',
      foundLinkPlural: 'tautan',
      noMatches: 'Tidak ada tautan yang cocok dengan',
      tableShortCode: 'Kode Pendek',
      tableOriginalUrl: 'URL Asli',
      tableClicks: 'Klik',
      tableCreated: 'Dibuat',
      tableStatus: 'Status',
      tableActions: 'Tindakan',
      statusExpired: 'Kedaluwarsa',
      statusActive: 'Aktif',
      actionSave: 'Simpan',
      actionCancel: 'Batal',
      actionCopy: 'Salin',
      actionCopied: 'Tersalin!',
      actionQr: 'QR',
      actionEdit: 'Ubah',
      actionManageRotator: 'Kelola Rotator',
      actionAnalytics: 'Analitik',
      actionDelete: 'Hapus',
      actionDeleting: 'Menghapus...',
      confirmTitle: 'Konfirmasi Hapus',
      deleteConfirmPrefix: 'Yakin ingin menghapus',
      deleteConfirmSuffix: 'Tindakan ini tidak dapat dibatalkan.',
      unknown: 'Tidak diketahui',
    },
    analytics: {
      accessDeniedTitle: 'Akses Ditolak',
      accessDeniedAuth: 'Autentikasi diperlukan. Silakan masuk untuk melihat analitik.',
      accessDeniedOwner: 'Akses ditolak. Anda hanya dapat melihat analitik tautan Anda sendiri.',
      signIn: 'Masuk',
      myDashboard: 'Dasbor Saya',
      backToDashboard: 'Kembali ke Dasbor',
      titlePrefix: 'Analitik untuk',
      rotatorBadge: 'Rotator',
      rotatorDestinationsTitle: 'Tujuan Rotasi',
      rotatorDestinationsToggle: 'Lihat tujuan rotasi',
      rotatorHitsLabel: 'Hit',
      totalClicks: 'Total Klik',
      created: 'Dibuat',
      status: 'Status',
      statusActive: 'Aktif',
      statusExpired: 'Kedaluwarsa',
      statusPermanent: 'Permanen',
      expires: 'Kedaluwarsa',
      utmTitle: 'Parameter UTM',
      utmNone: 'Tidak ada parameter UTM untuk tautan ini.',
      destinationUrl: 'URL Tujuan',
      clicksByDay: 'Klik per Hari',
      topReferrers: 'Perujuk Teratas',
      topBrowsers: 'Browser Teratas',
      topOS: 'Sistem Operasi Teratas',
      topDevices: 'Jenis Perangkat Teratas',
      noBrowserData: 'Belum ada data browser.',
      noOsData: 'Belum ada data OS.',
      noDeviceData: 'Belum ada data perangkat.',
      recentClicks: 'Klik Terbaru',
      tableTimestamp: 'Waktu',
      tableIp: 'Alamat IP',
      tableReferrer: 'Perujuk',
      tableLanguage: 'Bahasa',
      tableBrowser: 'Browser',
      tableOs: 'Sistem Operasi',
      tableDevice: 'Jenis Perangkat',
      tableUserAgent: 'User Agent',
      direct: 'Langsung',
      unknown: 'Tidak diketahui',
      expandRow: 'Perluas baris',
      collapseRow: 'Ciutkan baris',
      searchPlaceholder: 'Cari klik terbaru...',
      clear: 'Hapus',
      foundPrefix: 'Ditemukan',
      foundOf: 'dari',
      foundClickSingular: 'klik',
      foundClickPlural: 'klik',
      noMatches: 'Tidak ada klik yang cocok dengan',
      loading: 'Memuat...',
      prev: 'Sebelumnya',
      next: 'Selanjutnya',
      page: 'Halaman',
      of: 'dari',
      showing: 'Menampilkan',
      to: 'hingga',
      results: 'hasil',
      tableTime: 'Waktu',
      tableClickId: 'ID Klik',
      tableLocation: 'Lokasi',
      tableCountry: 'Negara',
      tableTraffic: 'Lalu lintas',
      human: 'Manusia',
      bot: 'Bot',
      trafficAll: 'Semua',
      trafficHumans: 'Manusia',
      trafficBots: 'Bot',
      allFields: 'Semua kolom',
      clearFilters: 'Hapus filter',
      noClicks: 'Tidak ada klik ditemukan.',
      loadFailed: 'Gagal memuat klik.',
      filterByValue: 'Filter dengan nilai ini',
      copy: 'Salin',
      copied: 'Tersalin',
    },
    adminUsers: {
      title: 'Kelola Pengguna',
      subtitle: 'Buat, perbarui, atau hapus pengguna.',
      backToAdmin: 'Kembali ke Admin',
      createTitle: 'Buat Pengguna',
      email: 'Email',
      name: 'Nama',
      password: 'Kata Sandi',
      role: 'Peran',
      roleUser: 'Pengguna',
      roleAdmin: 'Admin',
      createButton: 'Buat Pengguna',
      allTitle: 'Semua Pengguna',
      searchPlaceholder: 'Cari pengguna...',
      clear: 'Hapus',
      tableEmail: 'Email',
      tableName: 'Nama',
      tableRole: 'Peran',
      tableCreated: 'Dibuat',
      tableActions: 'Tindakan',
      actionSave: 'Simpan',
      actionCancel: 'Batal',
      actionEdit: 'Ubah',
      actionDelete: 'Hapus',
      noUsers: 'Tidak ada pengguna.',
      deleteConfirmPrefix: 'Hapus pengguna',
      deleteConfirmSuffix: 'Tindakan ini tidak dapat dibatalkan.',
      confirmTitle: 'Konfirmasi Hapus',
    },
    adminLinks: {
      title: 'Kelola Tautan',
      subtitle: 'Buat, perbarui, atau hapus URL pendek.',
      backToAdmin: 'Kembali ke Admin',
      createTitle: 'Buat Tautan',
      enterUrl: 'Masukkan URL panjang',
      showAdvanced: 'Tampilkan opsi lanjutan',
      hideAdvanced: 'Sembunyikan opsi lanjutan',
      customCode: 'Kode pendek kustom (opsional)',
      customHint: '3-20 karakter: huruf, angka, tanda hubung, garis bawah',
      owner: 'Pemilik (opsional)',
      anonymous: 'Anonim',
      expiration: 'Waktu Kedaluwarsa (Opsional)',
      neverExpire: 'Tidak kedaluwarsa',
      neverExpireNote: 'Tidak kedaluwarsa berarti tautan permanen',
      utmTitle: 'Parameter UTM (opsional)',
      utmPresets: 'Preset populer',
      utmNote: 'UTM ditambahkan ke URL tujuan untuk pelacakan kampanye.',
      utmMediumHelp: 'Saluran: cpc (iklan berbayar), email, social, organic, affiliate, referral',
      utmCampaignHelp:
        'Nama deskriptif: spring_sale_2026, product_launch_x, newsletter_q1, black_friday',
      utmTermHelp: 'Kata kunci target: running+shoes, marketing+software',
      utmContentHelp: 'Deskriptor elemen: header_button, text_link, banner_ad_blue, video_ad',
      presetManual: 'Manual / Tidak ada',
      presetNewsletter: 'Newsletter (email)',
      presetFacebook: 'Facebook (social)',
      presetFacebookAds: 'Facebook Ads (paid social)',
      presetInstagram: 'Instagram (social)',
      presetX: 'X / Twitter (social)',
      presetLinkedIn: 'LinkedIn (social)',
      presetGoogleAds: 'Google Ads (cpc)',
      presetWhatsapp: 'WhatsApp (messaging)',
      presetTelegram: 'Telegram (messaging)',
      presetLine: 'Line Messenger (messaging)',
      presetTiktokAds: 'TikTok Ads (paid social)',
      presetTiktokSocial: 'TikTok Social (social)',
      createButton: 'Buat Tautan',
      creating: 'Membuat...',
      bulkTitle: 'Pemendek URL Massal',
      bulkSubtitle: 'Pendekkan hingga 50 URL sekaligus. Satu URL per baris.',
      bulkLabel: 'URL yang akan dipendekkan (satu per baris)',
      bulkCountSuffix: 'URL',
      bulkSubmit: 'Pendekkan Semua URL',
      bulkProcessing: 'Memproses...',
      bulkErrorEmpty: 'Masukkan setidaknya satu URL',
      bulkErrorLimit: 'Maksimal 50 URL sekaligus',
      bulkSuccessPrefix: 'Berhasil memendekkan',
      bulkSuccessSuffix: 'URL',
      bulkSuccessSuffixPlural: 'URL',
      copyAll: 'Salin Semua',
      bulkFailedSingular: 'URL gagal',
      bulkFailedPlural: 'URL gagal',
      allLinksTitle: 'Semua Tautan',
      searchPlaceholder: 'Cari berdasarkan kode, URL, atau pemilik...',
      clear: 'Hapus',
      tableShortCode: 'Kode Pendek',
      tableOriginalUrl: 'URL Asli',
      tableOwner: 'Pemilik',
      tableClicks: 'Klik',
      tableCreated: 'Dibuat',
      tableActions: 'Tindakan',
      actionSave: 'Simpan',
      actionCancel: 'Batal',
      actionEdit: 'Ubah',
      actionDelete: 'Hapus',
      actionQr: 'QR',
      actionAnalytics: 'Analitik',
      deleteConfirmPrefix: 'Hapus',
      deleteConfirmSuffix: 'Tindakan ini tidak dapat dibatalkan.',
      noLinks: 'Tidak ada tautan.',
      expiresPlaceholder: 'Kedaluwarsa (jam)',
      directCopy: 'Salin',
      confirmTitle: 'Konfirmasi Hapus',
    },
    error: {
      title: 'Tautan Tidak Ditemukan',
      subtitle: 'Maaf, tautan yang Anda cari tidak ada atau telah dihapus.',
      whyTitle: 'Mengapa ini bisa terjadi?',
      reasonTyped: 'Tautan diketik salah atau tidak lengkap',
      reasonDeleted: 'Tautan telah dihapus oleh pemiliknya',
      reasonMissing: 'Tautan tidak pernah ada di sistem kami',
      goHome: 'Kembali ke Beranda',
      createLink: 'Buat Tautan',
    },
    rotator: {
      title: 'Smart URL',
      description: 'Rotasi beberapa URL tujuan dengan satu tautan pintar',
      slugLabel: 'Slug Rotator (opsional)',
      slugPlaceholder: 'rotasi-anda',
      slugHelp: 'Kosongkan untuk membuat slug otomatis.',
      typeLabel: 'Jenis Rotasi',
      typeSequential: 'Berurutan',
      typeRoundRobin: 'Round Robin',
      typeRandom: 'Acak',
      typeWeighted: 'Berbobot',
      typeSequentialHelp: 'URL diakses dalam urutan yang konsisten',
      typeRoundRobinHelp: 'Mendistribusikan klik secara merata berdasarkan total klik',
      typeRandomHelp: 'Setiap klik memilih URL acak',
      typeWeightedHelp: 'Distribusi berdasarkan bobot (probabilitas)',
      parentUrlLabel: 'Tautan Pendek Orang Tua',
      parentUrlPlaceholder: 'Pilih tautan pendek yang ada',
      parentUrlHelp: 'Pilih tautan pendek yang ingin Anda jadikan rotator',
      destinationsTitle: 'URL Tujuan',
      destinationsHelp: 'Tambahkan 2-5 URL tujuan. Satu URL per baris.',
      destinationsPlaceholder:
        'https://example.com/page-1\nhttps://example.com/page-2\nhttps://example.com/page-3',
      destinationsHint: 'Urutan baris digunakan untuk rotasi berurutan.',
      urlLabel: 'URL Tujuan',
      urlPlaceholder: 'https://example.com/page-',
      weightLabel: 'Bobot',
      weightHelp: 'Bobot lebih tinggi = lebih banyak klik (1-1000)',
      orderLabel: 'Urutan',
      orderHelp: 'Posisi dalam urutan (hanya untuk berurutan)',
      addDestination: 'Tambah URL Tujuan',
      removeDestination: 'Hapus',
      minUrlsError: 'Minimal 2 URL diperlukan',
      maxUrlsError: 'Maksimal 5 URL diperbolehkan',
      invalidWeightError: 'Bobot harus antara 1-1000',
      expirationLabel: 'Kedaluwarsa (Opsional)',
      expirationPlaceholder: 'Jam hingga kedaluwarsa',
      expirationHelp: 'Kosongkan untuk tidak pernah kedaluwarsa',
      utmTitle: 'Parameter UTM (opsional)',
      utmHelp: 'Parameter ini ditambahkan ke semua URL tujuan',
      createButton: 'Buat Rotasi',
      creating: 'Membuat...',
      loading: 'Memuat data rotasi...',
      updateButton: 'Perbarui Rotasi',
      updating: 'Memperbarui...',
      successCreated: 'Rotasi berhasil dibuat',
      successUpdated: 'Rotasi berhasil diperbarui',
      errorCreating: 'Gagal membuat rotasi',
      errorUpdating: 'Gagal memperbarui rotasi',
      manageTitle: 'Kelola Rotasi',
      editTitle: 'Edit Smart URL',
      rotationType: 'Jenis',
      urlCount: 'URL',
      totalHits: 'Total Hit',
      status: 'Status',
      statusActive: 'Aktif',
      statusInactive: 'Tidak Aktif',
      actions: 'Tindakan',
      actionEdit: 'Ubah',
      actionDelete: 'Hapus',
      actionView: 'Lihat',
      deleteConfirm: 'Yakin ingin menghapus rotasi ini?',
      noRotations: 'Anda belum membuat rotasi apa pun',
    },
  },
};

export function translate(locale: Locale, key: string) {
  const parts = key.split('.');
  let current: TranslationTree | string | undefined = translations[locale];
  for (const part of parts) {
    if (!current || typeof current === 'string') return key;
    current = current[part];
  }
  return typeof current === 'string' ? current : key;
}
